import { useEffect, useState } from "react";
import { Banknote, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rupiah } from "@/lib/api";
import { inputCls, btnPrimary, Skeleton } from "@/components/portal/ui";
import { monthLabel } from "@/pages/portal/Leaderboard";

const CATEGORIES = ["Persepuluhan", "Persembahan Syukur", "Misi", "Pembangunan", "Diakonia"];
const METHOD_LABEL = { online: "Online (gateway)", manual: "Transfer jemaat", offline: "Tunai / offline" };
const blank = () => ({ amount: "", category: CATEGORIES[0], activity: "", date: new Date().toISOString().slice(0, 10), counter1: "", counter2: "", note: "" });

export default function OfferingRecap() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/admin/offerings/recap?month=${month}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setData(null); load(); }, [month]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/offerings/manual", {
        amount: Number(form.amount), category: form.category, activity: form.activity,
        date: form.date, note: form.note, counters: [form.counter1, form.counter2].filter(Boolean),
      });
      toast.success("Rekap persembahan tersimpan");
      setForm(blank());
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/admin/offerings/manual/${id}`);
      toast.success("Rekap dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const valid = Number(form.amount) >= 1000 && form.activity.trim().length > 1 && form.counter1.trim();
  return (
    <div className="grid lg:grid-cols-[24rem_1fr] gap-6" data-testid="offering-recap">
      <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 space-y-3 self-start" data-testid="offline-offering-form">
        <p className="font-extrabold text-slate-900 flex items-center gap-2"><Banknote className="w-4 h-4 text-emerald-500" />Input Persembahan Offline</p>
        <p className="text-xs text-slate-500">Hasil hitung tunai. Nama petugas hitung wajib diisi (maksimal 2 orang).</p>
        <input type="number" className={inputCls} placeholder="Jumlah (Rp)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="offline-amount-input" />
        <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="offline-category-select">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input className={inputCls} placeholder="Kegiatan (mis. Ibadah Raya 1, Youth Night)" value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} data-testid="offline-activity-input" />
        <input type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="offline-date-input" />
        <div className="grid grid-cols-2 gap-2">
          <input className={inputCls} placeholder="Petugas hitung 1" value={form.counter1} onChange={(e) => setForm({ ...form, counter1: e.target.value })} data-testid="offline-counter1-input" />
          <input className={inputCls} placeholder="Petugas hitung 2" value={form.counter2} onChange={(e) => setForm({ ...form, counter2: e.target.value })} data-testid="offline-counter2-input" />
        </div>
        <input className={inputCls} placeholder="Catatan (opsional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="offline-note-input" />
        <button disabled={!valid || saving} onClick={save} className={`${btnPrimary} w-full !py-3`} data-testid="offline-submit"><Plus className="w-4 h-4" />Simpan Rekap</button>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-extrabold text-slate-900">Rekap Bulanan</p>
            <input type="month" className={`${inputCls} !w-40 !p-2.5 text-sm`} value={month} onChange={(e) => setMonth(e.target.value)} data-testid="recap-month-input" />
          </div>
          {!data ? <Skeleton className="h-40 mt-4" /> : (
            <>
              <p className="mt-4 text-3xl font-black text-slate-900 tabular-nums" data-testid="recap-total">{rupiah(data.total)}</p>
              <p className="text-xs text-slate-500">{data.count} transaksi · {monthLabel(data.month)}</p>
              <div className="mt-5 grid sm:grid-cols-3 gap-3">
                {["online", "manual", "offline"].map((m) => (
                  <div key={m} className="rounded-2xl bg-slate-50 p-4" data-testid={`recap-method-${m}`}>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{METHOD_LABEL[m]}</p>
                    <p className="mt-1 text-lg font-black text-slate-900 tabular-nums">{rupiah(data.by_method[m] || 0)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 grid md:grid-cols-2 gap-6">
                {[["Per Kegiatan", data.by_activity, "activity"], ["Per Kategori", data.by_category, "category"]].map(([title, obj, key]) => (
                  <div key={key}>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{title}</p>
                    <div className="mt-2 divide-y divide-slate-100" data-testid={`recap-by-${key}`}>
                      {Object.entries(obj).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                        <div key={k} className="py-2 flex items-center justify-between gap-3 text-sm"><span className="text-slate-600 truncate">{k}</span><span className="font-bold text-slate-900 tabular-nums shrink-0">{rupiah(v)}</span></div>
                      ))}
                      {Object.keys(obj).length === 0 && <p className="py-3 text-sm text-slate-400">Belum ada data.</p>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-testid="offline-entries">
          <p className="font-extrabold text-slate-900">Catatan Hitung Offline</p>
          {!data ? <Skeleton className="h-24 mt-4" /> : data.offline_entries.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500" data-testid="offline-entries-empty">Belum ada input offline bulan ini.</p>
          ) : (
            <div className="mt-3 divide-y divide-slate-100">
              {data.offline_entries.map((o) => (
                <div key={o.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`offline-entry-${o.id}`}>
                  <span className="flex-1 min-w-[12rem]">
                    <b className="text-slate-900 tabular-nums">{rupiah(o.amount)}</b>
                    <span className="block text-xs text-slate-500">{o.activity} · {o.category} · {o.date}</span>
                    <span className="block text-[11px] text-slate-400 inline-flex items-center gap-1 mt-0.5"><Users className="w-3 h-3" />{(o.counters || []).join(" & ")}</span>
                  </span>
                  <button onClick={() => remove(o.id)} className="inline-flex items-center gap-1.5 bg-red-50 text-red-600 rounded-xl px-3 py-2 text-sm font-bold" data-testid={`offline-delete-${o.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
